import { Link, Paper, Typography } from '@material-ui/core';
import { createStyles, StyledComponentProps, withStyles } from '@material-ui/core/styles';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import noItemsAfterFilter from '../../assets/noItemsAfterFilter.jpg';
import noItemsInCatalog from '../../assets/noItemsInCatalog.jpg';
import noSupplierOffers from '../../assets/noSupplierOffers.jpg';
import { ItemDetail } from '../../comps/ItemDetail';
import { OcButton } from '../../comps/OcButton';
import { OcDataview } from '../../comps/OcDataview';
import { OcDataviewFiltering } from '../../comps/OcDataviewFiltering';
import { PleaseWait } from '../../comps/PleaseWait';
import { IEntityStore, IItemCustomization, ISupplier, ISupplierItem, ITemplate, ITemplateItem } from '../../main/entities';
import { transl } from '../../main/transl';
import { mg } from '../../mg/mg';
import { DataviewStore } from '../../stores/DataviewStore';
import { getOmnistore } from '../../stores/Omnistore';
import { AdOrderingButton } from '../ads/AdsOrderingButton';
import { adOrderingStore, IAdOrderingItem } from '../ads/AdsOrderingStore';
import { EmptyTableRenderer } from '../EmptyTableRenderer';
import { OcScreen } from '../OcScreen';
import { ExternalTeamDialog } from '../team/TeamPage';
import { CheckoutCart } from './CheckoutCart';
import { IMySupplier, MiniCart } from './MiniCart';
import { OrderResultDialog } from './OrderResultDialog';
import { orderResultDialogStore } from './OrderResultDialogStore';
import { SetCategoryDialog, SetCategoryDialogStore } from './SetCategoryDialog';
import { TemplDetailsDialog } from './templ/TemplDetailsDialog';
import { TemplEditDialog } from './templ/TemplEditDialog';
import { TemplateDialog, templateDialogStore } from './TemplateDialog';

const dataviewStore = DataviewStore.create({
	id: 'LTemplates',
	orgColumns: [
		'_multiselect :multiselect',
		'supplierName',
		'name',
		'packaging',
		'itemNo',
		'category',
		'pricePair :pricePair',
		'amountToKeep :int 30',
		'amountToOrder :amountToOrder 30',
		'id 45',
		'addToCart :addToCart 30',
	],
	filtering: [
		{ columns: ['name', 'itemNo'], value: '', _label: transl('nameAndItemNo') },
		{ columns: ['category'], value: '', type: 'SELECT' }
	],
	isShowingFiltering: false,
	sorting: { column: 'name' },
	disabling: 'isInvalid',
	highlighting: 'isOffer',
	isShowingMultiselection: true,
});
dataviewStore.addColumnHiderToNamed('DEFAULT', 'itemNo', 'amountToKeep');
dataviewStore.addColumnHiderToNamed('HIDDEN', 'id');
dataviewStore.addColumnHiderToNamed('FIXED', 'supplierName');
dataviewStore.addColumnHiderToNamed('PRICE', 'pricePair');
dataviewStore.addColumnHiderToNamed('CATALOG', 'amountToOrder');
dataviewStore.addColumnHiderToNamed('NON_CATALOG', 'addToCart');
dataviewStore.addColumnHiderToNamed('NON_LTEMPLATES', '_multiselect');

const styles = createStyles({
});

interface LTemplatesProps {
}

@observer class _LTemplates extends OcScreen<LTemplatesProps & StyledComponentProps> {
	@computed get multirepo() {
		return getOmnistore().multirepo;
	}

	@observable private showSetCategoryDialogStore: boolean = false;

	@action setShowSetCategoryDialogStore(showSetCategoryDialogStore: boolean) {
		this.showSetCategoryDialogStore = showSetCategoryDialogStore;
	}

	@observable setCategoryDialogStore = SetCategoryDialogStore.create({});

	@computed get locationStore() {
		return getOmnistore().locationStore;
	}

	@computed get reoctoStore() {
		return getOmnistore().reoctoStore;
	}

	@computed get orderingStore() {
		return getOmnistore().appPageStores.orderingStore;
	}

	name() {
		return 'LTemplates';
	}

	@computed get _templates(): Array<ITemplate> {
		const templateRepo = this.multirepo.templateRepo;
		return templateRepo.etag ? templateRepo.allAsList.filter(t => t.branchId == this.locationStore.branchId) : [];
	}

	@computed get _suppliers(): Array<ISupplier> {
		const supplierRepo = this.multirepo.supplierRepo;
		const a: Array<ISupplier | undefined> = this._templates.map(t => t.supplierId).map(supplierRepo.byId);
		return a.filter(mg.isPresent);
	}

	@computed get _uniqueTemplate(): ITemplate | undefined {
		const templates = this._templates.filter(t => t.supplierId === this._selectedSupplierId || !this._selectedSupplierId);
		return templates.length == 1 ? templates[0] : undefined;
	}

	@computed get _adOrderingItems(): Array<IAdOrderingItem> {
		const t = this._uniqueTemplate;
		if (!t) return [];
		return adOrderingStore.byTemplateId(t.id);
	}

	@observable extendTeam: boolean = false;

	submenu() {
		const locationStore = this.locationStore;
		return dataviewStore.isShowingSubmenu === false ? [] : [
			locationStore.page === 'Checkout' && !this._selectedSupplierId ? null :
				<OcDataviewFiltering key="filtering" state={dataviewStore} repo={this._repo} />,
			locationStore.page !== 'LTemplates' ? null : {
				name: 'LCatalog',
				disabled: !locationStore.supplierId && this._suppliers.length > 1 ? transl('chooseASupplier') : '',
				onClick: () => locationStore.setPage('LCatalog'),
			},
			locationStore.page !== 'LTemplates' ? null : {
				name: 'SetCategory',
				disabled: !dataviewStore.nMultiselected ? transl('SetCategory.selectSomeLines') : '',
				onClick: () => {
					this.multirepo.categoryRepo(locationStore.branchId).fetch();
					const ids = dataviewStore.allMultiselectedIds;
					this.setShowSetCategoryDialogStore(true);
					this.setCategoryDialogStore.start(this._itemsAsList.filter(e => ids.has(e.id)))
						.finally(() => this.setShowSetCategoryDialogStore(false))
						.then(() => dataviewStore.clearMultiselected())
						.catch(() => { });
				},
			},
			locationStore.page !== 'LTemplates' ? null : {
				name: 'templDetails',
				disabled: !this.orderingStore.uniqueTemplate ? transl('chooseASupplier') : '',
				onClick: () => {
					const os = this.orderingStore;
					const t = os.uniqueTemplate;
					if (mg.assertPresent(t)) os.templDetailsStore.start(t);
				},
			},
			locationStore.page !== 'LTemplates' ? null : {
				name: 'createTemplate',
				onClick: () => templateDialogStore.start(2),
			},
			locationStore.page !== 'LTemplates' ? null : {
				name: 'team.extendTeam',
				disabled: !this.orderingStore.uniqueTemplate ? transl('chooseASupplier') : '',
				onClick: () => runInAction(() => this.extendTeam = true),
			},
			...this._adOrderingItems
				.filter(i => locationStore.page !== 'LCatalog')
				.map(i => <AdOrderingButton key={'AdOrderingButton' + i.supplierItemId} item={i} store={adOrderingStore} />),
		];
	}

	@computed get _supplierIdToSupplierName() {
		const supplierRepo = this.multirepo.supplierRepo;
		return mg.entriesToObject(this._suppliers.map(s => {
			const supplier = supplierRepo.byId(s.id);
			return [s.id, supplier ? supplier.name : '?'];
		}));
	}

	@computed get _templateIdToSupplierName() {
		const supplierRepo = this.multirepo.supplierRepo;
		return mg.entriesToObject(this._templates.map(t => {
			const supplier = supplierRepo.byId(t.supplierId);
			return [t.id, supplier ? supplier.name : '?'];
		}));
	}

	@computed get _sortedTemplates() {
		return this._templates.slice().sort(mg.comparatorOn(t => this._templateIdToSupplierName[t.id]));
	}

	@computed get _selectedSupplierId() {
		return this.locationStore.supplierId;
	}

	@computed get _templateItemStores(): Array<IEntityStore<ITemplateItem>> {
		return this._templates.map(t => 'TemplateItem:' + t.id).map(getOmnistore().entityManagerStore.byStoreId) as any;
	}

	@computed get _offerStores(): Array<IEntityStore<ISupplierItem>> {
		return this._suppliers.map(s => 'SpecialOffer:' + s.id).map(getOmnistore().entityManagerStore.byStoreId) as any;
	}

	@computed get _catalogStores(): Array<IEntityStore<ISupplierItem>> {
		return this._suppliers.map(s => 'SupplierItem:' + s.id).map(getOmnistore().entityManagerStore.byStoreId) as any;
	}

	_usedStores = (): Array<string | { id: string, etag: string }> => {
		const supplierRepo = this.multirepo.supplierRepo;
		const templateRepo = this.multirepo.templateRepo;
		const itemCustomizationRepo = this.multirepo.itemCustomizationRepo;
		const locationStore = this.locationStore;

		const templates = this._templates.filter(t => !this._selectedSupplierId || t.supplierId === this._selectedSupplierId);
		const suppliers = this._suppliers.filter(s => !this._selectedSupplierId || s.id === this._selectedSupplierId);

		const templateItemStores = templates.map(t => 'TemplateItem:' + t.id);
		const offerStores = locationStore.page !== 'SupplierOffers' ? [] : suppliers.map(s => 'SpecialOffer:' + s.id);
		const catalogStores = locationStore.page !== 'LCatalog' ? [] : suppliers.map(s => 'SupplierItem:' + s.id);

		const categoryRepo = !locationStore.branchId ? null : this.multirepo.categoryRepo(locationStore.branchId);

		const result = [templateRepo, supplierRepo, itemCustomizationRepo, ...templateItemStores, ...offerStores, ...catalogStores, categoryRepo]
			.filter(mg.isPresent);
		// console.log('_usedStores', result.map(s => typeof s==='string' ? s : s.id).join(' '))
		return result;
	}

	usedStores = (): Array<{ id: string, etag: string }> => this._usedStores()
		.map((s) => typeof s === 'string' ? getOmnistore().entityManagerStore.byStoreId(s) : s)

	@computed get _supplierIdToTemplateIdMap() {
		const result = {};
		this._templates.forEach(t => result[t.supplierId] = t.id);
		return result;
	}

	private _elementCache = new WeakMap()

	@computed get _itemCustomizations(): Map<number, IItemCustomization> {
		return new Map(this.multirepo.itemCustomizationRepo.allAsList
			.filter(o => o.branchId === this.locationStore.branchId)
			.map(o => [o.supplierItemId, o]));
	}

	_templateItemToElement(e: ITemplateItem) {
		let result = this._elementCache.get(e);
		if (result) return result;

		const templateRepo = this.multirepo.templateRepo;
		const template = templateRepo.byId(e.templateId);
		const supplierId = template ? template.supplierId : 0;
		const supplierName = this._templateIdToSupplierName[e.templateId];
		const itemCustomization = this._itemCustomizations.get(e.supplierItemId);
		const retailPrice = itemCustomization?.retailPrice ?? null;
		const isInvalid = e.isInvalid || itemCustomization?.isAllowed===false;
		result = { ...e, templateItemId: e.id, supplierId, supplierName, isInvalid, retailPrice };

		this._elementCache.set(e, result);
		return result;
	}

	_supplierItemToElement(e: ISupplierItem) {
		let result = this._elementCache.get(e);
		if (result) return result;

		const templateId = this._supplierIdToTemplateIdMap[e.supplierId];
		const supplierName = this._supplierIdToSupplierName[e.supplierId];
		const itemCustomization = this._itemCustomizations.get(e.id);
		const retailPrice = itemCustomization?.retailPrice ?? null;
		const isInvalid = e.isDeleted || itemCustomization?.isAllowed===false;
		result = { ...e, id: -e.id, supplierItemId: e.id, supplierName, templateId, retailPrice, isInvalid };

		this._elementCache.set(e, result);
		return result;
	}

	@computed get _itemsAsList() {
		return this._templateItemStores.flatMap((s: IEntityStore<ITemplateItem>) => s.allAsList).map(e => this._templateItemToElement(e));
	}

	@computed get _offersAsList() {
		return this._offerStores.flatMap((s: IEntityStore<ISupplierItem>) => s.allAsList).map(e => this._supplierItemToElement(e));
	}

	@computed get _catalogsAsList() {
		return this._catalogStores.flatMap((s: IEntityStore<ISupplierItem>) => s.allAsList).map(e => this._supplierItemToElement(e));
	}

	@computed get _allAsList() {
		const bySupplierItemId = new Map();
		this._itemsAsList.forEach(ti => bySupplierItemId.set(ti.supplierItemId, ti));
		this._catalogsAsList.forEach(si => {
			const ti = bySupplierItemId.get(si.supplierItemId);
			const e = { amountToOrder: 0, amountToKeep: 0, category: si.itemGroup1, ...si, ...ti, timestamp: null, listPrice: si.price };
			bySupplierItemId.set(e.supplierItemId, e);
		});
		this._offersAsList.forEach(si => {
			const ti = bySupplierItemId.get(si.supplierItemId);
			const e = { amountToOrder: 0, amountToKeep: 0, category: '', ...si, ...ti, timestamp: null, isOffer: true, listPrice: si.price };
			bySupplierItemId.set(e.supplierItemId, e);
		});
		return [...bySupplierItemId.values()];
	}

	@computed get _extSuppliers(): Array<IMySupplier> {
		const map = mg.entriesToObject(this._suppliers.map(s =>
			[s.id, Object.assign({ nPricedItems: 0, nUnpricedItems: 0, priceSum: 0 }, s)]));
		this._allAsList.filter(i => i.amountToOrder > 0).forEach(i => {
			const value = map[i.supplierId];
			if (!mg.isPresent(value)) return;
			const price = i.retailPrice || i.listPrice;
			if (price) {
				value.nPricedItems += +i.amountToOrder;
				value.priceSum += i.amountToOrder * price;
			} else {
				value.nUnpricedItems += +i.amountToOrder;
			}
		});

		const result: Array<IMySupplier> = Object.values(map);
		result.sort(mg.comparatorOn(o => o.name));

		const locationStore = this.locationStore;
		if (result.length > 1 && (locationStore.page !== 'LCatalog' || this.reoctoStore.isTester)) {
			result.unshift(({
				id: 0,
				name: transl('allSuppliers'),
				nPricedItems: result.map((e: IMySupplier) => e.nPricedItems).reduce((a, b) => a + b, 0),
				nUnpricedItems: result.map((e: IMySupplier) => e.nUnpricedItems).reduce((a, b) => a + b, 0),
				priceSum: result.map((e: IMySupplier) => e.priceSum).reduce((a, b) => a + b, 0),
			}) as any);
		}
		result.forEach((e: IMySupplier) => e.priceText = e.priceSum > 0 ? 'EUR ' + e.priceSum.toFixed(3).replace('.', ',') : '');
		return result;
	}

	@action _toggleSelectedSupplierId = (id: number) => {
		const s = this.locationStore;
		s.setSupplierId(id === s.supplierId && s.page === 'Checkout' ? 0 : id);
	}

	@computed get _hasPrice() {
		for (let e of this._allAsList) {
			if (e.retailPrice || e.listPrice) return true;
		}
		return false;
	}

	autoruns() {
		const locationStore = this.locationStore;
		const templateRepo = this.multirepo.templateRepo;
		const supplierRepo = this.multirepo.supplierRepo;
		return [
			() => {
				if (!templateRepo.etag) return;
				if (!supplierRepo.etag) return;
				this._suppliers.map(e => e.id).indexOf(this._selectedSupplierId) === -1 && this._toggleSelectedSupplierId(0);
			},
			() => {
				dataviewStore.setIgnoredColumnHiders([
					this._hasPrice ? 'PRICE' : null,
					!this._selectedSupplierId && this._suppliers.length > 1 ? 'FIXED' : null,
					locationStore.page === 'LCatalog' ? 'NON_CATALOG' : 'CATALOG',
					locationStore.page === 'LTemplates' ? 'NON_LTEMPLATES' : null,
				]);
			},
		];
	}

	reactions() {
		const locationStore = this.locationStore;
		return [
			{
				when: () => [locationStore.page, locationStore.branchId, locationStore.supplierId],
				then: () => {
					dataviewStore.clearMultiselected();
					dataviewStore.clearFiltering();
				},
			},
			{
				when: () => [this._suppliers],
				then: () => {
					this._suppliers.map(s => getOmnistore().entityManagerStore.get('SpecialOffer', s.id))
						.filter(s => !s.etag)
						.forEach(s => s.fetch());
				}
			},
			{
				when: () => [this._templates],
				then: () => {
					this._templates.map(s => getOmnistore().entityManagerStore.get('TemplateItem', s.id))
						.filter(s => !s.etag)
						.forEach(s => s.fetch());
				}
			},
		];
	}

	_pageFilter = (page: string) => {
		switch (page) {
		case 'LTemplates': return e => e.templateItemId;
		case 'SupplierOffers': return e => e.isOffer;
		case 'Checkout': return e => e.templateItemId && e.amountToOrder > 0;
		case 'LCatalog': return e => true;
		}
		console.error('_pageFilter: page', page);
		return e => false;
	}

	@computed get _etag() {
		//console.log('Unfetched-stores', this.usedStores().filter(s => s.etag).map(s => s.id).join(','));
		const list = this.usedStores().map(s => s.etag);
		for (let e of list) {
			if (!e) return '';
		}
		return list.sort().join(',');
	}

	@computed get _prefiltered() {
		const locationStore = this.locationStore;
		return this._allAsList.filter(this._pageFilter(locationStore.page));
	}

	@action _onTemplateItemEdit = (entity, changes: { [s: string]: any }) => {
		const templateId = entity.templateId || this._templates.filter(t => t.supplierId === entity.supplierId)[0].id;
		const store = getOmnistore().entityManagerStore.get('TemplateItem:' + templateId);
		const id = entity.id > 0 ? entity.id : undefined;
		const timestamp = '';
		const e = { ...entity, ...changes, id, timestamp };
		console.log('_onTemplateItemEdit', e);
		if (e.id) {
			store.upserter.update(e);
		} else {
			store.upserter.create(e);
		}
	}

	@action _checkSupplierCartEmpty = (supplierId, amountToOrder) => {
		var result = this._allAsList.filter(e => (e.supplierId === supplierId && e.amountToOrder > 0 ));
		if (result.length > 1) return false;

		const supplier = this._extSuppliers.filter((s: any) => s.id === supplierId)[0]; //TODO
		if (amountToOrder === 0 && (supplier.nPricedItems + supplier.nUnpricedItems > 0)) return true;
		return false;
	}

	@action _onTemplateItemEditCheckout = (entity, changes: { [s: string]: any }) => {
		const templateId = entity.templateId || this._templates.filter(t => t.supplierId === entity.supplierId)[0].id;
		const store = getOmnistore().entityManagerStore.get('TemplateItem:' + templateId);
		const id = entity.id > 0 ? entity.id : undefined;
		const timestamp = '';
		const e = { ...entity, ...changes, id, timestamp };

		const isSupplierCartEmpty = this._checkSupplierCartEmpty(entity.supplierId, e.amountToOrder);
		var isUpdateAmountToOrder = true;
		if (isSupplierCartEmpty){
			isUpdateAmountToOrder = confirm(transl('updateCart'));
			if (isUpdateAmountToOrder){
				this._toggleSelectedSupplierId(0);
			}
		}

		if (isUpdateAmountToOrder){
			if (e.id) {
				store.upserter.update(e);
			} else {
				store.upserter.create(e);
			}
		}
	}

	@computed get _repo() {
		return {
			etag: this._etag,
			allAsList: this._prefiltered
				.filter(e => !this._selectedSupplierId || e.supplierId === this._selectedSupplierId),
			onEdit: this._onTemplateItemEdit
		};
	}

	@computed get _isFilteredOut() {
		return !!this._repo.allAsList.length;
	}

	_noDataImage = (page: string) => {
		if (this._isFilteredOut) return noItemsAfterFilter;
		switch (page) {
		case 'LTemplates': return '';
		case 'SupplierOffers': return noSupplierOffers;
		case 'Checkout': return '';
		case 'LCatalog': return noItemsInCatalog;
		}
		console.error('UnknownPage', page);
		return '';
	}

	_emptyRenderer = () =>
		<EmptyTableRenderer page={this.locationStore.page} src={this._noDataImage(this.locationStore.page)} isFilteredOut={this._isFilteredOut} />

	@computed get _selectedEntity() {
		const id = dataviewStore.selectedEntityId;
		if (!id) return null;
		for (let e of this._allAsList) {
			if (e.id === id) return e;
		}
		console.warn('Entity not found by id:', id);
		return null;
	}

	@computed get _templateItemRepo() {
		if (!this._selectedEntity) return undefined;
		return getOmnistore().entityManagerStore.get('TemplateItem', this._selectedEntity.templateId);
	}

	@computed get _canSupplierOffers(): boolean {
		if (!this._selectedSupplierId) return !!this._offersAsList.length;
		return !!getOmnistore().entityManagerStore.get('SpecialOffer', this._selectedSupplierId).allAsList.length;
	}

	@computed get _canCheckout() {
		if (this._adOrderingItems.length) return true;
		for (let e of this._allAsList) {
			if (e.amountToOrder > 0) return true;
		}
		return false;
	}

	@computed get _canCartCheckout() {
		if (this._adOrderingItems.length) return true;
		for (let e of this._allAsList) {
			if (e.amountToOrder > 0) return true;
		}
		return false;
	}

	@computed get _prevPage(): string {
		const page = this.locationStore.page;
		switch (page) {
		case 'LTemplates': return '';
		case 'SupplierOffers': return 'LTemplates';
		case 'Checkout': return this._canSupplierOffers ? 'SupplierOffers' : 'LTemplates';
		case 'LCatalog': return 'LTemplates';
		}
		console.error('Unknown page', page);
		return '';
	}

	@computed get _nextPage(): string {
		const page = this.locationStore.page;
		switch (page) {
		case 'LTemplates': return this._canSupplierOffers ? 'SupplierOffers' : this._canCheckout ? 'Checkout' : '';
		case 'SupplierOffers': return this._canCheckout ? 'Checkout' : '';
		case 'Checkout': return '';
		case 'LCatalog': return '';
		}
		console.error('Unknown page', page);
		return '';
	}

	@action.bound gotoNextPage() {
		this.locationStore.setPage(this._nextPage);
	}

	_renderCart() {
		const ss: { [s: string]: React.CSSProperties } = {
			root: {
				display: 'flex',
				flexDirection: 'column',
				flex: '0 0 140px',
				alignItems: 'stretch',
				minHeight: 0,
			},
			outer: {
				position: 'relative',
				flex: 1,
				height: 'calc(100% - 60px)',
				overflowX: 'hidden',
				overflowY: 'auto',
			},
			inner: {
				position: 'absolute',
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			},
			button: {
				minHeight: 60,
				marginTop: 20,
				marginBottom: 20,
			},
		};

		return <Paper style={ss.root}>
			<div style={ss.outer}>
				<div style={ss.inner}>{
					this._extSuppliers.map((s: IMySupplier) => <MiniCart key={s.id} supplier={s}
						isSelected={s.id === this._selectedSupplierId}
						isOnly={this._extSuppliers.length === 1}
						onClick={this._toggleSelectedSupplierId} />)
				}</div>
			</div>
			<OcButton variant="contained" color="primary" onClick={this.gotoNextPage} style={ss.button} disabled={!this._nextPage} disabledText={transl('order.emptyCart')}>
				<Typography variant="h6">{transl('doOrder')}</Typography>
			</OcButton>

		</Paper>;
	}

	_backToList = () => {
		this.locationStore.setPage('LTemplates');
	}

	_renderCheckoutCart = (template) => {
		const supplierId = template.supplierId;
		const supplier = this._extSuppliers.filter((s: IMySupplier) => s.id === supplierId)[0];
		if (!supplier) return null;
		// if (!supplier.nPricedItems && !supplier.nUnpricedItems) return null; //TODO this would show nothing which is worse than an empty cart

		const onSelect = () => this._toggleSelectedSupplierId(supplierId);
		const repo = {
			etag: this._repo.etag,
			allAsList: this._repo.allAsList.filter(i => i.supplierId === supplierId),
			onEdit: this._onTemplateItemEditCheckout,
		};
		return <CheckoutCart
			key={template.id}
			template={template}
			supplier={supplier}
			repo={repo}
			adOrderingItems={adOrderingStore.byTemplateId(template.id)}
			dataviewStore={dataviewStore}
			isSelected={this._selectedSupplierId === supplierId}
			onSelect={onSelect} />;
	}

	_renderCheckout() {
		if (!this._repo.etag) return <PleaseWait />;

		const templates = this._sortedTemplates.length > 1 && this._selectedSupplierId
			? this._sortedTemplates.filter(t => t.supplierId === this._selectedSupplierId)
			: this._sortedTemplates.filter(t => true); //TODO

		return <div style={{
			height: '100%', display: 'flex', flexDirection: 'column',
			overflowY: 'scroll', overflowX: 'hidden', paddingRight: '15px'
		}}>
			{
				this._canCartCheckout ?
					templates.map(this._renderCheckoutCart)
					:
					<>
						<Link onClick={this._backToList} style={{ cursor: 'pointer' }}>{transl('goBackToList')}</Link>
						{this._emptyRenderer()}
					</>
			}
			<OrderResultDialog store={orderResultDialogStore} />
		</div>;
	}

	renderBody() {
		if (this.locationStore.page === 'Checkout') return this._renderCheckout();

		return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', marginTop: 10 }}>
			<div style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
				{
					!this._repo.etag ? <PleaseWait /> :
						<OcDataview repo={this._repo} state={dataviewStore} emptyRenderer={this._emptyRenderer} />
				}
				<div style={{ flex: '0 0 8px' }} />
				{this._renderCart()}
			</div>
			<ItemDetail state={dataviewStore} entity={this._selectedEntity} repo={this._templateItemRepo as IEntityStore} categories={this.multirepo.currentCategories} fetchCurrentCategories={this.multirepo.fetchCurrentCategories}/>
			<TemplateDialog store={templateDialogStore} />
			<TemplDetailsDialog store={this.orderingStore.templDetailsStore} />
			<TemplEditDialog store={this.orderingStore.templDetailsStore} />
			<SetCategoryDialog
				store={this.setCategoryDialogStore}
				isOpen={this.showSetCategoryDialogStore}
				categories={this.multirepo.currentCategories}
				fetchCurrentCategories={this.multirepo.fetchCurrentCategories}
			/>
			{!this.extendTeam ? null : <ExternalTeamDialog template={this.orderingStore.uniqueTemplate!} onClose={() => runInAction(() => this.extendTeam = false)}/>}
		</div>;
	}
}

export const LTemplates = withStyles(styles)(_LTemplates);
